/**
 * LaSea Custom Auth Client
 * Self-contained authentication using PostgreSQL via the Cloudflare Worker proxy.
 * No external auth services needed — passwords are hashed with bcrypt on the server side.
 */

const WORKER_URL = import.meta.env.VITE_WORKER_URL;
const WORKER_SECRET = import.meta.env.VITE_WORKER_SECRET;

const authHeaders = {
  'Content-Type': 'application/json',
  'X-Worker-Auth': WORKER_SECRET,
};

export const neonAuthClient = {

  signInWithPassword: async ({ email, password }) => {
    try {
      const response = await fetch(`${WORKER_URL}/auth/login`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Login failed');

      if (result.token) {
        localStorage.setItem('lasea-auth-token', result.token);
        localStorage.setItem('lasea-auth-user', JSON.stringify(result.user));
      }

      return { data: { user: result.user, session: result }, error: null };
    } catch (error) {
      return { data: { user: null, session: null }, error };
    }
  },

  signUp: async ({ email, password, options }) => {
    try {
      const response = await fetch(`${WORKER_URL}/auth/register`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          email,
          password,
          name: options?.data?.full_name || email.split('@')[0],
          role: options?.data?.role || 'customer',
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Signup failed');

      return { data: { user: result.user, session: result }, error: null };
    } catch (error) {
      return { data: { user: null, session: null }, error };
    }
  },

  signOut: async () => {
    localStorage.removeItem('lasea-auth-token');
    localStorage.removeItem('lasea-auth-user');
    return { error: null };
  },

  getSession: async () => {
    try {
      const token = localStorage.getItem('lasea-auth-token');
      const user = localStorage.getItem('lasea-auth-user');
      if (!token || !user) return { data: { session: null }, error: null };

      return {
        data: {
          session: {
            access_token: token,
            user: JSON.parse(user),
          },
        },
        error: null,
      };
    } catch {
      return { data: { session: null }, error: null };
    }
  },

  onAuthStateChange: (callback) => {
    const token = localStorage.getItem('lasea-auth-token');
    const user = localStorage.getItem('lasea-auth-user');

    if (token && user) {
      try {
        callback('SIGNED_IN', { access_token: token, user: JSON.parse(user) });
      } catch {
        callback('SIGNED_OUT', null);
      }
    } else {
      callback('SIGNED_OUT', null);
    }

    return { data: { subscription: { unsubscribe: () => {} } } };
  },

  updateUser: async (attributes) => {
    return { data: null, error: new Error('updateUser not implemented') };
  },

  resetPasswordForEmail: async (email) => {
    return { data: null, error: new Error('resetPassword not implemented') };
  },
};
